import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { supabaseClient as supabase } from "@/lib/supabase";

interface InitiateFreemiumSignupParams {
    email: string;
    onProgress?: (step: string) => void;
}

/**
 * Free-first clinician signup: anonymous auth with onboarding_step=trial,
 * no Stripe checkout. Trial billing is provisioned by DB trigger / RPC.
 */
export const useInitiateFreemiumSignupMutation = () => {
    const navigate = useNavigate();

    return useMutation({
        mutationKey: ["initiate-freemium-signup"],
        mutationFn: async ({ email, onProgress }: InitiateFreemiumSignupParams) => {
            onProgress?.("Creating your free account...");

            const { data: authData, error: authError } = await supabase.auth.signInAnonymously({
                options: {
                    data: {
                        email,
                        role: "CLINICIAN",
                        onboarding_step: "trial",
                    },
                },
            });

            if (authError || !authData?.user || !authData?.session) {
                throw authError || new Error("Failed to create anonymous session");
            }

            const { error: usersError } = await supabase.from("users").upsert({
                user_id: authData.user.id,
                email,
            });

            if (usersError) {
                console.error("⚠️ Error updating users table:", usersError);
            }

            onProgress?.("Setting up your free plan...");

            const { error: billingError } = await supabase.rpc("provision_trial_billing", {
                p_user_id: authData.user.id,
            });

            if (billingError) {
                console.error("⚠️ Error provisioning trial billing:", billingError);
                throw new Error(billingError.message || "Failed to set up free plan");
            }

            return { email, userId: authData.user.id };
        },
        onSuccess: (data) => {
            void navigate({
                to: "/verify-email",
                search: { email: data.email },
            });
        },
    });
};

export default useInitiateFreemiumSignupMutation;
